// Ephemeral editor session state — current tool, in-progress drawing, selection.
// Never persisted. UI modules subscribe to re-render on change.
import type { Point, ShapeKind } from "./types";

export type EditorMode =
  | "select"
  | "place"
  | "shape"
  | "tensator"
  | "measure"
  | "calibrate";
export type Selection = { type: "marker" | "shape"; id: string } | null;
export type LayerKey = "objekte" | "flaechen" | "tensatoren";

interface SessionState {
  mode: EditorMode;
  placeCat: string | null;
  multiPlace: boolean;
  shapeKind: ShapeKind;
  shapeCat: string;
  lineClosed: boolean;
  shapeDraft: Point[];
  /** live cursor position while drawing a shape/tensator (image coords) */
  hoverPoint: Point | null;
  /** true when the cursor is within snapping range of the first draft point */
  snapClose: boolean;
  measurePts: Point[];
  calibratePts: Point[];
  selection: Selection;
  /** which layers are drawn on the plan */
  layers: Record<LayerKey, boolean>;
  /** public share view — every mutation path must check this and refuse */
  readOnly: boolean;
}

const s: SessionState = {
  mode: "select",
  placeCat: null,
  multiPlace: false,
  shapeKind: "object",
  shapeCat: "",
  lineClosed: false,
  shapeDraft: [],
  hoverPoint: null,
  snapClose: false,
  measurePts: [],
  calibratePts: [],
  selection: null,
  layers: { objekte: true, flaechen: true, tensatoren: true },
  readOnly: false,
};

const listeners = new Set<() => void>();
export function onSession(fn: () => void): () => void {
  listeners.add(fn);
  return () => listeners.delete(fn);
}
export function emitSession(): void {
  for (const fn of listeners) fn();
}

export function session(): Readonly<SessionState> {
  return s;
}

export function resetTransient(): void {
  s.shapeDraft = [];
  s.hoverPoint = null;
  s.snapClose = false;
  s.measurePts = [];
  s.calibratePts = [];
}

export function setHover(point: Point | null, snapClose = false): void {
  const changed =
    !!s.hoverPoint !== !!point ||
    (point && s.hoverPoint && (point.x !== s.hoverPoint.x || point.y !== s.hoverPoint.y)) ||
    s.snapClose !== snapClose;
  s.hoverPoint = point;
  s.snapClose = snapClose;
  if (changed) emitSession();
}

// Lets interaction.ts commit an in-progress tensator chain before the mode
// actually switches (resetTransient would otherwise discard the draft).
let beforeModeLeave: ((from: EditorMode, to: EditorMode) => void) | null = null;
export function setBeforeModeLeave(fn: (from: EditorMode, to: EditorMode) => void): void {
  beforeModeLeave = fn;
}

/** Called once at boot for the public share view — before anything else runs. */
export function setReadOnly(v: boolean): void {
  s.readOnly = v;
}

export function setMode(mode: EditorMode, placeCat: string | null = null): void {
  if (s.readOnly && mode !== "select") return;
  if (mode !== s.mode) beforeModeLeave?.(s.mode, mode);
  s.mode = mode;
  s.placeCat = placeCat;
  if (mode !== "select") s.selection = null;
  resetTransient();
  emitSession();
}

export function clearDraft(): void {
  s.shapeDraft = [];
  emitSession();
}

export function setShapeCat(id: string): void {
  s.shapeCat = id;
  emitSession();
}

export function setLineClosed(v: boolean): void {
  s.lineClosed = v;
}

export function setMultiPlace(v: boolean): void {
  s.multiPlace = v;
}

export function setLayer(key: LayerKey, visible: boolean): void {
  s.layers[key] = visible;
  emitSession();
}
export function toggleLayer(key: LayerKey): void {
  s.layers[key] = !s.layers[key];
  emitSession();
}

export function select(sel: Selection): void {
  s.selection = sel;
  emitSession();
}

export function deselect(): void {
  if (!s.selection) return;
  s.selection = null;
  emitSession();
}

export function pushDraftPoint(p: Point): void {
  s.shapeDraft.push(p);
  emitSession();
}

export function pushMeasurePoint(p: Point): void {
  if (s.measurePts.length >= 2) s.measurePts = [];
  s.measurePts.push(p);
  emitSession();
}

export function pushCalibratePoint(p: Point): void {
  if (s.calibratePts.length >= 2) s.calibratePts = [];
  s.calibratePts.push(p);
  emitSession();
}
